/**
 * Setting up TallyPrime, step by step (docs/tally/09). Feature branch only.
 *
 *   1 Download Connector · 2 Install · 3 Enter pairing code ·
 *   4 Choose Tally company · 5 Check totals · 6 Connect
 *
 * Nothing here pretends: the connector is a pilot preview, not a signed
 * installer; a pairing code says when it expires; the company list is what
 * the connector actually found, with nothing chosen for the owner; the totals
 * are exactly what was read; and nothing becomes the receivables source until
 * the owner ticks "This is the company I want to connect".
 */
import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Disclosure, cx } from '@timely/ui';
import { useFormatters } from '../../book/useFormatters';
import { FormNotice } from '../../screens/AuthLayout';
import { chooseCompany, createPairingCode, type ConnectorStatus } from '../useConnector';
import { DEFAULT_RECEIVABLES_SETTINGS, describeSettingsChange, type ReceivablesSettings } from '@timely/domain';
import { PreviewReceivables } from '../../receivables/PreviewReceivables';
import { useReceivablesSettings } from '../../receivables/useReceivablesSettings';
import { TROUBLESHOOTING, ago } from './tallyCopy';

export const STEPS = ['Download Connector', 'Install', 'Enter pairing code', 'Choose Tally company', 'Check totals', 'Connect'] as const;

/** Where the owner is in the six steps (0-based), for the step list. */
export function StepList({ current }: { current: number }) {
    return (
        <ol aria-label="Setting up TallyPrime" className="flex flex-wrap gap-x-5 gap-y-2 mb-6">
            {STEPS.map((step, i) => (
                <li key={step} aria-current={i === current ? 'step' : undefined}
                    className={cx('flex items-center gap-2 text-secondary', i === current ? 'text-label font-semibold' : i < current ? 'text-label-2' : 'text-label-3')}>
                    <span aria-hidden="true" className={cx('inline-flex items-center justify-center w-5 h-5 rounded-full text-caption font-semibold',
                        i < current ? 'bg-pos-bg text-pos' : i === current ? 'bg-accent text-on-accent' : 'bg-card-3 text-label-3')}>
                        {i < current ? '✓' : i + 1}
                    </span>
                    {step}
                </li>
            ))}
        </ol>
    );
}

/** Step 0: what this is, before anything happens. */
export function TallyIntro({ sheetConnected, onStart, canManage }: { sheetConnected: boolean; onStart: () => void; canManage: boolean }) {
    return (
        <Card className="p-5 sm:p-6">
            <h3 className="text-heading font-semibold text-label">Connect TallyPrime</h3>
            <ul className="mt-3 space-y-2 text-body text-label-2 list-disc pl-5">
                <li>Timely Payment reads your receivables from TallyPrime: customer balances, bills and due dates.</li>
                <li>It never changes anything in TallyPrime — no accounts, no vouchers, no settings.</li>
                <li>Timely Connector runs on the Windows computer where you use TallyPrime, and reads it there. For now, TallyPrime and the connector must be on the same computer.</li>
                <li>There is nothing to open on your network: no public IP address, no port forwarding, no VPN.</li>
            </ul>
            {sheetConnected && (
                <p className="mt-4 text-body text-label-2 bg-card-3 rounded-control px-3.5 py-3">
                    Your receivables come from a Google Sheet today. Before TallyPrime replaces it you will review how your accounts match,
                    so every follow-up, note, promise and cheque stays with the right customer. Nothing changes until you confirm.
                </p>
            )}
            {canManage ? (
                <div className="mt-5"><Button variant="primary" onClick={onStart}>Start</Button></div>
            ) : (
                <p className="mt-5 text-secondary text-label-3">Somebody who manages receivables data can connect TallyPrime.</p>
            )}
        </Card>
    );
}

/** Mm:ss until a moment, ticking once a second; null once it has passed. */
function useCountdown(until: string | null): string | null {
    const [now, setNow] = useState(() => Date.now());
    useEffect(() => {
        if (!until) return;
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [until]);
    if (!until) return null;
    const left = Math.max(0, Math.floor((Date.parse(until) - now) / 1000));
    if (left === 0) return null;
    return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;
}

/** Steps 1–3: the connector, and the one-time code it is paired with. */
export function PairingStep({ workspaceId, onWaiting }: { workspaceId: string; onWaiting: (waiting: boolean) => void }) {
    const [code, setCode] = useState<{ code: string; expiresAt: string } | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const left = useCountdown(code?.expiresAt ?? null);
    const expired = code !== null && left === null;

    useEffect(() => { onWaiting(code !== null && !expired); }, [code, expired, onWaiting]);

    const make = async () => {
        if (busy) return;
        setBusy(true);
        setError(null);
        try { setCode(await createPairingCode(workspaceId)); } catch (e) { setError(e instanceof Error ? e.message : 'A pairing code could not be made just now.'); } finally { setBusy(false); }
    };

    return (
        <div className="grid gap-5 lg:grid-cols-2">
            <Card className="p-5 sm:p-6">
                <div className="flex items-center gap-2">
                    <h3 className="text-heading font-semibold text-label">1–2 · Timely Connector for Windows</h3>
                    <Badge tone="warn">Pilot preview</Badge>
                </div>
                <p className="mt-2 text-body text-label-2">
                    Install it on the computer where you use TallyPrime. It runs quietly in the background, reads TallyPrime there, and never changes it.
                </p>
                <p className="mt-3 text-secondary text-label-3">
                    The signed Windows installer is not published yet. During the pilot, Timely support installs the connector with you; it takes about five minutes.
                </p>
                {import.meta.env.DEV && (
                    <Disclosure className="mt-4" title="Developer preview (local builds only)">
                        <p className="text-secondary text-label-2">On the computer with TallyPrime, from this repository:</p>
                        <pre className="mt-2 text-caption bg-card-3 rounded-control p-3 overflow-x-auto"><code>{`pnpm --filter @timely/connector connector pair --cloud ${window.location.origin} --code ${code?.code ?? 'XXXX-XXXX'}
pnpm --filter @timely/connector connector run`}</code></pre>
                    </Disclosure>
                )}
            </Card>

            <Card className="p-5 sm:p-6">
                <h3 className="text-heading font-semibold text-label">3 · Pairing code</h3>
                {error && <div className="mt-3"><FormNotice tone="error">{error}</FormNotice></div>}
                {!code ? (
                    <>
                        <p className="mt-2 text-body text-label-2">When Timely Connector asks for a pairing code, make one here. It works once, for 15 minutes.</p>
                        <div className="mt-4"><Button variant="primary" busy={busy} onClick={() => void make()}>Make a pairing code</Button></div>
                    </>
                ) : (
                    <>
                        <p className="mt-2 text-body text-label-2">Enter this code in Timely Connector:</p>
                        <p data-testid="pairing-code" aria-label={`Pairing code ${code.code.split('').join(' ')}`}
                            className={cx('mt-3 font-mono text-[2rem] leading-none font-semibold tracking-[0.18em] select-all', expired ? 'text-label-3 line-through' : 'text-label')}>
                            {code.code}
                        </p>
                        {expired ? (
                            <p role="status" className="mt-3 text-body text-dang">This code has expired. Make a new one.</p>
                        ) : (
                            <p className="mt-3 text-secondary text-label-3">
                                Expires in <span className="num font-semibold text-label-2" data-testid="pairing-expiry">{left}</span> (at {new Date(code.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}).
                                It works once, and is never shown again.
                            </p>
                        )}
                        {!expired && (
                            <p role="status" className="mt-4 text-body text-label-2 flex items-center gap-2">
                                <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse" />
                                Waiting for Timely Connector… this page moves on by itself.
                            </p>
                        )}
                        <div className="mt-4">
                            <Button variant="secondary" busy={busy} onClick={() => void make()}>Make a new code</Button>
                            <span className="ml-3 text-caption text-label-3">The earlier code stops working.</span>
                        </div>
                    </>
                )}
                <p className="mt-5 text-caption text-label-3">Pairing never gives the connector a password, a database key, or access to anything but this company's receivables.</p>
            </Card>
        </div>
    );
}

/** Step 4: the companies the connector found in TallyPrime. Nothing is chosen for the owner. */
export function CompanyStep({ workspaceId, status, onChosen }: { workspaceId: string; status: ConnectorStatus; onChosen: () => void }) {
    const companies = status.discoveredCompanies ?? null;
    const [choice, setChoice] = useState<string | null>(status.sourceCompany?.id ?? null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const tallyDown = status.lastReport?.tallyReachable === false;
    const trouble = (id: string) => TROUBLESHOOTING.find(t => t.id === id)!;

    const use = async () => {
        if (!choice || busy) return;
        setBusy(true);
        setError(null);
        try { await chooseCompany(workspaceId, status.id, choice); onChosen(); } catch (e) { setError(e instanceof Error ? e.message : 'That company could not be chosen just now.'); } finally { setBusy(false); }
    };

    return (
        <Card className="p-5 sm:p-6">
            <h3 className="text-heading font-semibold text-label">4 · Choose the TallyPrime company</h3>
            <p className="mt-1 text-secondary text-label-3">Timely Connector is paired on “{status.deviceName}”.</p>
            {error && <div className="mt-3"><FormNotice tone="error">{error}</FormNotice></div>}
            {companies === null ? (
                <div role="status" className="mt-4 text-body text-label-2">
                    {tallyDown ? (
                        <><p className="font-semibold text-label">{trouble('not-running').title} on that computer.</p><p className="mt-1">{trouble('not-running').body}</p></>
                    ) : 'Looking for TallyPrime on that computer…'}
                </div>
            ) : companies.length === 0 ? (
                <div role="status" className="mt-4 text-body text-label-2">
                    <p className="font-semibold text-label">{trouble('no-company').title}.</p><p className="mt-1">{trouble('no-company').body}</p>
                </div>
            ) : (
                <fieldset className="mt-4">
                    <legend className="text-body text-label-2">Companies open in TallyPrime now. Timely will read only the one you choose — even if another is open on screen.</legend>
                    <div className="mt-3 grid gap-2">
                        {companies.map(c => (
                            <label key={c.guid} className={cx('flex items-start gap-3 rounded-control border px-3.5 py-3 cursor-pointer',
                                choice === c.guid ? 'border-accent bg-accent-tint' : 'border-separator hover:bg-hover')}>
                                <input type="radio" name="tally-company" value={c.guid} checked={choice === c.guid} onChange={() => setChoice(c.guid)} className="mt-1" />
                                <span className="min-w-0">
                                    <span className="block text-body font-semibold text-label break-words">{c.name}</span>
                                    <span className="block text-caption text-label-3">
                                        {c.booksFrom ? `Books from ${c.booksFrom} · ` : ''}Identity <span className="font-mono">{c.guid.slice(0, 8)}…</span>
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className="mt-4"><Button variant="primary" disabled={!choice} busy={busy} onClick={() => void use()}>Use this company</Button></div>
                </fieldset>
            )}
        </Card>
    );
}

/** Between choosing and the preview: the connector is reading the company. */
export function ReadingStep({ status }: { status: ConnectorStatus }) {
    const failed = status.lastError ?? status.lastReport?.lastError ?? null;
    return (
        <Card className="p-5 sm:p-6">
            <h3 className="text-heading font-semibold text-label">5 · Reading {status.sourceCompany?.name}</h3>
            {failed ? (
                <div className="mt-3"><FormNotice tone="error">{failed}</FormNotice></div>
            ) : (
                <p role="status" className="mt-3 text-body text-label-2 flex items-center gap-2">
                    <span aria-hidden="true" className="inline-block w-2 h-2 rounded-full bg-accent animate-pulse" />
                    Timely Connector is reading the company’s receivables from TallyPrime. This usually takes under a minute.
                </p>
            )}
        </Card>
    );
}

const BUCKETS = [['1-45', '1–45 days'], ['46-90', '46–90 days'], ['91-135', '91–135 days'], ['>135', 'Over 135 days']] as const;

/** Step 5: exactly what was read, for the owner to confirm. */
export function PreviewStep({
    workspaceId, canManage = true, status, needsReview, busy, onConfirm, onChooseAgain,
}: {
    workspaceId: string;
    canManage?: boolean;
    status: ConnectorStatus;
    /** The book has accounts (or a sheet): the next step is the account review, not the connection. */
    needsReview: boolean;
    busy: boolean;
    onConfirm: () => void;
    onChooseAgain: () => void;
}) {
    const { money, date } = useFormatters();
    const [sure, setSure] = useState(false);
    const settings = useReceivablesSettings(workspaceId);
    const [draft, setDraft] = useState<ReceivablesSettings | null>(null);
    const [saving, setSaving] = useState(false);
    const [choiceError, setChoiceError] = useState<string | null>(null);
    const preview = status.preview!;
    const s = preview.summary;
    const net = s.receivable - s.creditAmount;
    const now = useMemo(() => new Date(), [status.previewAt]);
    const intelligence = preview.intelligence;
    // Timely cannot tell what it would connect: say so, and do not offer the connection.
    const blocked = intelligence?.findings.blocked ?? false;
    // A company connecting for the first time chooses how its receivables are aged; Connect saves that choice first,
    // so the first update already reads them its way (docs/tally/18 §1.3). A database without 0036 has no choice to make.
    const mustChoose = Boolean(intelligence) && canManage && settings.available && !settings.loading && settings.current.version === 0;
    const confirm = async () => {
        if (mustChoose) {
            if (!draft) return;
            setSaving(true); setChoiceError(null);
            try {
                await settings.save(draft, 'Chosen when connecting TallyPrime', { changes: describeSettingsChange(DEFAULT_RECEIVABLES_SETTINGS, draft), connecting: true });
            } catch (e) {
                setChoiceError(e instanceof Error ? e.message : 'How receivables are aged could not be saved just now. Nothing was connected.');
                setSaving(false);
                return;
            }
            setSaving(false);
        }
        onConfirm();
    };
    return (
        <Card className="p-5 sm:p-6">
            <h3 className="text-heading font-semibold text-label">5 · Check the totals from TallyPrime</h3>
            <dl className="mt-4 grid gap-x-6 gap-y-3 sm:grid-cols-2 text-body">
                <div><dt className="text-caption text-label-3">TallyPrime company</dt><dd className="font-semibold text-label break-words">{preview.company.name}</dd></div>
                <div><dt className="text-caption text-label-3">Identity in TallyPrime</dt><dd className="font-mono text-secondary text-label-2 break-all">{preview.company.id}</dd></div>
                <div><dt className="text-caption text-label-3">Read</dt><dd className="text-label-2">{ago(status.previewAt, now, date)} · balances as of {date.day(preview.asOf)}</dd></div>
                <div><dt className="text-caption text-label-3">Computer</dt><dd className="text-label-2 break-words">{status.deviceName}</dd></div>
            </dl>

            <div className="mt-5 grid gap-3 grid-cols-2 lg:grid-cols-4">
                {[
                    ['Customer accounts with a balance', String(s.accounts)],
                    ['Total receivable (net)', money.balance(Math.abs(net), net < 0 ? 'Cr' : 'Dr')],
                    ['Owed to you (debit balances)', money.exact(s.receivable)],
                    [`In credit (${s.creditAccounts} account${s.creditAccounts === 1 ? '' : 's'})`, money.exact(s.creditAmount)],
                ].map(([label, value]) => (
                    <div key={label} className="rounded-control bg-card-3 px-3.5 py-3">
                        <div className="text-caption text-label-3">{label}</div>
                        <div className="mt-1 text-heading font-semibold text-label num">{value}</div>
                    </div>
                ))}
            </div>

            {intelligence ? (
                <PreviewReceivables intelligence={intelligence} accounts={preview.accounts} canManage={canManage} settings={settings}
                    mustChoose={mustChoose} onDraft={setDraft} />
            ) : (<>
            <table className="mt-5 w-full text-body" aria-label="Ageing by bill date">
                <caption className="text-left text-caption text-label-3 pb-2">Ageing by bill date</caption>
                <tbody>
                    {BUCKETS.map(([key, label]) => (
                        <tr key={key} className="border-t border-separator">
                            <th scope="row" className="py-2 font-normal text-label-2 text-left">{label}</th>
                            <td className="py-2 text-right num text-label">{money.exact(s.ageing[key] ?? 0)}</td>
                        </tr>
                    ))}
                    <tr className="border-t border-separator">
                        <th scope="row" className="py-2 font-normal text-label-2 text-left">Not aged <span className="text-caption text-label-3">(on account, or not tracked bill by bill)</span></th>
                        <td className="py-2 text-right num text-label">{money.balance(Math.abs(s.unaged), s.unaged < 0 ? 'Cr' : 'Dr')}</td>
                    </tr>
                </tbody>
            </table>

            {preview.diagnostics.length > 0 && (
                <ul className="mt-4 space-y-1 text-secondary text-label-2 list-disc pl-5">
                    {preview.diagnostics.map(d => <li key={d}>{d}</li>)}
                </ul>
            )}
            </>)}

            {blocked && (
                <div className="mt-5"><FormNotice tone="error">Timely cannot tell what it would connect from this read. See “What Timely found” above, then read the company again.</FormNotice></div>
            )}

            <label className="mt-5 flex items-start gap-3 text-body text-label cursor-pointer">
                <input type="checkbox" checked={sure} onChange={e => setSure(e.target.checked)} className="mt-1" />
                <span>This is the company I want to connect.</span>
            </label>
            <div className="mt-4 flex flex-wrap items-center gap-3">
                <Button variant="primary" disabled={!sure || blocked || settings.loading || (mustChoose && !draft)} busy={busy || saving} onClick={() => void confirm()}>{needsReview ? 'Review how accounts match' : 'Connect TallyPrime'}</Button>
                <Button variant="secondary" onClick={onChooseAgain}>Choose a different company</Button>
            </div>
            {mustChoose && !draft && <p className="mt-3 text-secondary text-label-3">Choose how receivables are aged, above, to connect.</p>}
            {choiceError && <div className="mt-3"><FormNotice tone="error">{choiceError}</FormNotice></div>}
            {needsReview && <p className="mt-3 text-secondary text-label-3">Your book already has accounts. Next you will see how they match TallyPrime’s ledgers; nothing changes until you connect.</p>}
        </Card>
    );
}
